import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ModalProvider } from "./util/modal/ModalProvider.tsx";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import "@fontsource/figtree/300.css";
import "@fontsource/figtree/400.css";
import "@fontsource/figtree/500.css";
import "@fontsource/figtree/700.css";
import NavigationContainer from "./containers/NavigationContainer";
import SolutionsContainer from "./containers/SolutionsContainer.tsx";
import { solutionRoutes } from "./data/solutionRoutes.tsx";

import TargetForm from "./pages/TargetForm";
import BaselineForm from "./pages/BaselineForm";
import Reporting from "./pages/Reporting";
import ERPCaseStudy from "./pages/ERPCaseStudy";
import ReportingCaseStudy from "./pages/ReportingCaseStudy";
import SolutionsLanding from "./pages/SolutionsLanding";
import Projects from "./pages/Projects.tsx";
import Organization from "./pages/Organization.tsx";
import SitesMetadataForm from "./pages/SitesMetadataForm.tsx";
import ProjectsPlanner from "./pages/ProjectsPlanner.tsx";
import SitesConsumptionForm from "./pages/SitesConsumptionForm.tsx";
import { theme } from "./muiTheme.ts";
import ContactUsForm from "./pages/ContactUsForm.tsx";
import EmailPlanForm from "./pages/EmailPlanForm.tsx";
import Scope3Info from "./pages/Scope3Info.tsx";
import DashboardOld from "./pages/DashboardOld.tsx";
import { EmailPlanPanel } from "./components/dashboardPanels/EmailPlanPanel.tsx";
import { RecommendationsPanel } from "./components/dashboardPanels/RecommendationsPanel.tsx";
import { ReviewAndAdjustPanel } from "./components/dashboardPanels/ReviewAndAdjustPanel.tsx";
import LandingPage from "./pages/landingZone/LandingPage.tsx";
import GuidedTourPage from "./pages/landingZone/GuidedTourPage.tsx";
import LearnMorePage from "./pages/landingZone/LearnMorePage.tsx";
import DashboardPage from "./pages/landingZone/DashboardPage.tsx";

const router = createBrowserRouter([
  // landing pages uses a separate template so they can have a nice hero header
  // and they are split out so that we can have a different header and footer
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/learn-more",
    element: <LearnMorePage />,
  },
  {
    path: "/guided-tour",
    element: <GuidedTourPage />,
  },
  // dashboard also uses this template now. eventually the whole app will, and then
  // we can call it something else
  {
    path: "/dashboard",
    element: <DashboardPage />,
    children: [
      {
        path: "",
        element: <ReviewAndAdjustPanel />,
      },
      {
        path: "email-plan",
        element: <EmailPlanPanel />,
      },
      {
        path: "recommendations",
        element: <RecommendationsPanel />,
      },
    ],
  },
  ////////////////////
  {
    element: <NavigationContainer />,
    children: [
      { path: "/reporting", element: <Reporting /> },
      { path: "/dashboard-old", element: <DashboardOld /> },

      { path: "/target", element: <TargetForm /> },
      { path: "/plan", element: <SolutionsLanding /> },
      {
        path: "/plan/entry",
        element: <SolutionsContainer />,
        children: solutionRoutes,
      },
      { path: "/plan/scope3", element: <Scope3Info /> },
      { path: "/baseline", element: <BaselineForm /> },
      { path: "/erp-case-study", element: <ERPCaseStudy /> },
      { path: "/reporting-case-study", element: <ReportingCaseStudy /> },
      { path: "/projects", element: <Projects /> },
      { path: "/projects/planner", element: <ProjectsPlanner /> },
      { path: "/organization", element: <Organization /> },
      { path: "/sites", element: <SitesMetadataForm /> },
      { path: "/sites/consumption", element: <SitesConsumptionForm /> },
      { path: "/contact-us", element: <ContactUsForm /> },
      { path: "/email-plan", element: <EmailPlanForm /> },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ModalProvider>
          <CssBaseline />
          <RouterProvider router={router} />
        </ModalProvider>
      </LocalizationProvider>
    </ThemeProvider>
  </StrictMode>,
);
